import { Controller } from "@hotwired/stimulus"
import { readShareParams } from "../modules/salary_lab/url_state"

/**
 * Handles save/unsave (bookmark) toggle for SalaryLab calculations.
 * POSTs to /salary-lab/save-calculation with current calculator params,
 * DELETEs to /salary-lab/save-calculation/:id to unsave.
 */
export default class extends Controller {
  static targets = ["button", "iconOutline", "iconFilled", "label"]
  static values = {
    calculatorType: String,
    saveUrl: String,
    savedId: Number,
    saved: { type: Boolean, default: false }
  }

  toggle() {
    if (this.savedValue) {
      this._unsave()
    } else {
      this._save()
    }
  }

  async _save() {
    this._setLoading(true)

    const params = this._gatherParams()
    if (!params) {
      this._setLoading(false)
      return
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ type: this.calculatorTypeValue, ...params })
      })

      if (response.ok) {
        const data = await response.json()
        this.savedIdValue = data.id
        this.savedValue = true
        this._updateUI(true)
      } else if (response.status === 401) {
        window.location.href = "/users/sign_in"
      } else {
        const data = await response.json().catch(() => ({}))
        this._showError(data.error || "Unable to save calculation")
      }
    } catch {
      this._showError("Network error — please try again")
    } finally {
      this._setLoading(false)
    }
  }

  async _unsave() {
    if (!this.savedIdValue) return
    this._setLoading(true)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`${this.saveUrlValue}/${this.savedIdValue}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        this.savedValue = false
        this.savedIdValue = 0
        this._updateUI(false)
      }
    } catch {
      this._showError("Network error — please try again")
    } finally {
      this._setLoading(false)
    }
  }

  _gatherParams() {
    const params = readShareParams(this.calculatorTypeValue)
    return Object.keys(params).length > 0 ? params : null
  }

  _updateUI(saved) {
    if (this.hasIconOutlineTarget && this.hasIconFilledTarget) {
      this.iconOutlineTarget.classList.toggle("hidden", saved)
      this.iconFilledTarget.classList.toggle("hidden", !saved)
    }
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = saved ? "Saved" : "Save"
    }
    if (this.hasButtonTarget) {
      if (saved) {
        this.buttonTarget.classList.add("text-cyan-700", "border-cyan-300")
        this.buttonTarget.classList.remove("text-gray-600", "border-gray-300")
      } else {
        this.buttonTarget.classList.remove("text-cyan-700", "border-cyan-300")
        this.buttonTarget.classList.add("text-gray-600", "border-gray-300")
      }
    }
  }

  _setLoading(loading) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
    }
  }

  _showError(message) {
    // Brief visual feedback — could be enhanced with a toast system
    if (this.hasLabelTarget) {
      const original = this.labelTarget.textContent
      this.labelTarget.textContent = message
      setTimeout(() => { this.labelTarget.textContent = original }, 3000)
    }
  }
}
